<template>
  <div class="web-chat-overlay" v-if="visible">
    <div class="web-chat-panel">
      <div class="web-chat-header">
        <span>在线客服</span>
        <span class="web-chat-status" :class="{ offline: !isLoggedIn }">{{ isLoggedIn ? '在线' : '离线' }}</span>
        <button type="button" class="web-chat-close" @click="close">&times;</button>
      </div>
      <div class="web-chat-messages" ref="messages">
        <div class="web-chat-time">今天 {{ timeStr }}</div>
        <!-- 欢迎消息 -->
        <div v-if="messages.length === 0" class="web-chat-welcome">
          <div class="welcome-icon">客</div>
          <p class="welcome-text">您好，欢迎咨询 FTX 交易平台！我是智能客服小 F，请问有什么可以帮您？</p>
        </div>
        <!-- 消息列表 -->
        <div v-for="(m, i) in messages" :key="i" :class="['web-chat-bubble', m.from === 'bot' ? 'bot' : 'user']">
          <div class="web-chat-avatar">{{ m.from === 'bot' ? '客' : '我' }}</div>
          <div class="web-chat-text-wrap">
            <!-- 推理内容（深度思考） -->
            <div v-if="m.reasoning" class="web-chat-reasoning" @click="m.showReasoning = !m.showReasoning">
              <div class="reasoning-toggle">
                <i :class="m.showReasoning ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
                <span>思考过程</span>
              </div>
              <div v-show="m.showReasoning" class="reasoning-content">{{ m.reasoning }}</div>
            </div>
            <!-- 正文 -->
            <div class="web-chat-text" v-html="renderMarkdown(m.text)"></div>
            <!-- 加载动画 -->
            <div v-if="m.loading" class="web-chat-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
        <!-- 未登录提示 -->
        <div v-if="!isLoggedIn && messages.length === 0" class="web-chat-login-hint">
          <p>请先登录后使用 AI 客服</p>
        </div>
      </div>
      <div class="web-chat-quick" v-if="!loading">
        <button v-for="q in quickBtns" :key="q" @click="sendQuick(q)">{{ q }}</button>
      </div>
      <div class="web-chat-input-bar">
        <input
          type="text"
          v-model="input"
          placeholder="请输入您的问题..."
          maxlength="500"
          @keyup.enter="sendMsg"
          :disabled="loading || !isLoggedIn"
        />
        <button
          type="button"
          class="web-chat-send"
          @click="loading ? cancelChat() : sendMsg()"
          :disabled="!isLoggedIn"
        >
          {{ loading ? '停止' : '发送' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { sendChatStream, getChatModels, cancelChatRun } from '@/api/chat'
import store from '@/store'

export default {
  name: 'ChatOverlay',
  props: { visible: Boolean },
  data() {
    return {
      messages: [],
      input: '',
      quickBtns: ['如何入金', '如何出金', '杠杆说明', '账户安全'],
      timeStr: '',
      loading: false,
      sessionId: null,
      runId: null,
      abortController: null,
      modelId: null,
    }
  },
  computed: {
    isLoggedIn() {
      return !!store.getters['user/token']
    }
  },
  created() {
    const d = new Date()
    this.timeStr = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  },
  watch: {
    visible(v) {
      if (v) {
        this.$nextTick(() => this.scrollBottom())
        if (!this.modelId) {
          this.loadModels()
        }
      }
    }
  },
  methods: {
    close() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      this.loading = false
      this.$emit('close')
    },

    async loadModels() {
      try {
        const res = await getChatModels()
        if (res.code === 200 && res.data && res.data.length > 0) {
          this.modelId = res.data[0].modelId
        }
      } catch (e) {
        console.warn('[Chat] 加载模型失败:', e)
      }
    },

    sendMsg() {
      const t = this.input.trim()
      if (!t || this.loading) return
      this.input = ''
      this.doSend(t)
    },

    sendQuick(q) {
      if (this.loading) return
      this.doSend(q)
    },

    async doSend(text) {
      if (!this.isLoggedIn) {
        this.$root.$emit('open-login')
        return
      }

      // 添加用户消息
      this.messages.push({ from: 'user', text })
      this.scrollBottom()

      // 添加 AI 占位消息
      const aiMsg = { from: 'bot', text: '', reasoning: '', loading: true, showReasoning: false }
      this.messages.push(aiMsg)
      const aiIndex = this.messages.length - 1
      this.loading = true

      this.abortController = new AbortController()

      let aiContent = ''
      let aiReasoning = ''

      try {
        await sendChatStream({
          message: text,
          sessionId: this.sessionId,
          modelId: this.modelId,
        }, {
          onContent: (content) => {
            aiContent += content
            this.$set(this.messages[aiIndex], 'text', aiContent)
            this.scrollBottom()
          },
          onReasoning: (content) => {
            aiReasoning += content
            this.$set(this.messages[aiIndex], 'reasoning', aiReasoning)
            this.scrollBottom()
          },
          onMeta: (sessionId) => {
            this.sessionId = sessionId
          },
          onRunInfo: (runId) => {
            this.runId = runId
          },
          onMetrics: (metrics) => {
            this.$set(this.messages[aiIndex], 'metrics', metrics)
          },
          onError: (error) => {
            this.$set(this.messages[aiIndex], 'text', error || '抱歉，服务暂时不可用，请稍后重试')
            this.$set(this.messages[aiIndex], 'loading', false)
          },
          onDone: () => {
            this.$set(this.messages[aiIndex], 'loading', false)
            this.loading = false
            this.abortController = null
            this.scrollBottom()
          },
          signal: this.abortController.signal,
        })
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.$set(this.messages[aiIndex], 'text', '网络连接失败，请重试')
        }
        this.$set(this.messages[aiIndex], 'loading', false)
        this.loading = false
        this.abortController = null
      }
    },

    cancelChat() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      if (this.runId) {
        cancelChatRun(this.runId).catch(() => {})
        this.runId = null
      }
      this.loading = false
      const last = this.messages[this.messages.length - 1]
      if (last && last.from === 'bot' && last.loading) {
        this.$set(last, 'loading', false)
        if (!last.text) {
          this.$set(last, 'text', '已停止生成')
        }
      }
    },

    renderMarkdown(text) {
      if (!text) return ''
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')
      return html
    },

    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messages
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.web-chat-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.web-chat-panel {
  width: 420px;
  max-width: 96vw;
  height: 560px;
  max-height: 90vh;
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.web-chat-header {
  position: relative;
  padding: 16px 48px 16px 20px;
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid var(--border);
  text-align: center;
}

.web-chat-status {
  position: absolute;
  right: 44px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: var(--green);
  font-weight: 600;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: var(--green);
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
  }

  &.offline {
    color: #999;

    &::before {
      background: #999;
    }
  }
}

.web-chat-close {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #999;
}

.web-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f7f7f7;
}

.web-chat-time {
  text-align: center;
  font-size: 11px;
  color: var(--text-muted);
}

.web-chat-welcome {
  text-align: center;
  padding: 30px 20px;

  .welcome-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--red-soft);
    color: var(--red);
    font-size: 20px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
  }

  .welcome-text {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.6;
  }
}

.web-chat-bubble {
  display: flex;
  gap: 8px;
  max-width: 88%;

  &.user {
    flex-direction: row-reverse;
    align-self: flex-end;
  }
}

.web-chat-avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--red-soft);
  color: var(--red);
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;

  .user & {
    background: var(--red);
    color: #fff;
  }
}

.web-chat-text-wrap {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
}

.web-chat-text {
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.55;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  word-break: break-word;

  .user & {
    background: var(--red);
    color: #fff;
  }

  ::v-deep code {
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 13px;

    .user & {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  ::v-deep strong {
    font-weight: 600;
  }
}

.web-chat-reasoning {
  margin-bottom: 6px;
  border: 1px dashed #ddd;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;

  .reasoning-toggle {
    padding: 6px 10px;
    font-size: 12px;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    gap: 4px;

    i {
      font-size: 12px;
    }
  }

  .reasoning-content {
    padding: 6px 10px;
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.5;
    background: #fafafa;
    border-top: 1px dashed #eee;
    white-space: pre-wrap;
  }
}

.web-chat-typing {
  display: inline-flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ccc;
    animation: typing-dot 1.2s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing-dot {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1);
  }
}

.web-chat-quick {
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  overflow-x: auto;
  border-top: 1px solid var(--border);
  background: #fff;

  button {
    flex-shrink: 0;
    padding: 6px 12px;
    border: 1px solid var(--border);
    border-radius: 16px;
    background: #fff;
    font-size: 12px;
    color: var(--text-secondary);
    cursor: pointer;
    font-family: inherit;

    &:hover {
      border-color: var(--red);
      color: var(--red);
    }
  }
}

.web-chat-input-bar {
  display: flex;
  gap: 8px;
  padding: 10px 12px 14px;
  background: #fff;
  border-top: 1px solid var(--border);

  input {
    flex: 1;
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 10px 14px;
    font-size: 14px;
    outline: none;
    font-family: inherit;

    &:focus {
      border-color: var(--red);
    }

    &:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }
  }
}

.web-chat-send {
  flex-shrink: 0;
  padding: 0 16px;
  border: none;
  border-radius: 20px;
  background: var(--red);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.web-chat-login-hint {
  text-align: center;
  padding: 20px;

  p {
    font-size: 13px;
    color: var(--text-muted);
  }
}
</style>
